import React from 'react';
import { ChevronDownIcon as ChevronDown } from '@heroicons/react/20/solid';
import { ChevronUpIcon as ChevronUp } from '@heroicons/react/20/solid';

import { flexRender } from '@tanstack/react-table';

import tw, { css } from 'twin.macro';

interface Props {
  tableInstance: any;
  action?: any;
  condensed?: boolean;
  striped?: boolean;
  hideSort?: boolean;
  firstCol?: React.FC<{ id: string }>;
}

export default function ReactTable(props: Props) {
  const { condensed, striped, hideSort } = props;
  const instance = props.tableInstance;
  return (
    <div tw="flex flex-col">
      <div>
        <div tw="align-middle min-w-full">
          <div tw="screen:shadow overflow-y-hidden print:overflow-x-hidden screen:overflow-x-auto print:border print:rounded-lg border-b border-neutral-quiet sm:rounded-lg">
            <table tw="divide-y divide-gray-200 min-w-full">
              <thead tw="bg-neutral-white">
                {instance.getHeaderGroups().map(headerGroup => (
                  <tr key={headerGroup.id}>
                    {props.firstCol && <th />}
                    {headerGroup.headers.map(header => (
                      <th
                        key={header.id}
                        style={{
                          width: header.getSize(),
                        }}
                        css={[
                          tw`px-6 py-3 text-left text-xs font-medium text-neutral-quiet uppercase tracking-wider whitespace-nowrap`,
                          condensed && tw`py-1 px-1`,
                          header.id === 'hiddenaction' && tw`hidden`,
                        ]}
                      >
                        <div
                          css={[
                            header.column.getCanSort() &&
                              tw`cursor-pointer select-none`,
                            tw`flex items-center`,
                          ]}
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          <span
                            css={[
                              condensed &&
                                css`
                                  font-size: 80%;
                                `,
                              tw`whitespace-normal`,
                            ]}
                          >
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                          </span>
                          {!hideSort && (
                            <span>
                              {/* eslint-disable-next-line no-nested-ternary */}
                              {{
                                desc: <ChevronDown tw="h-4 w-4 pb-0.5" />,
                                asc: <ChevronUp tw="h-4 w-4 pb-0.5" />,
                              }[header.column.getIsSorted() as string] ?? null}
                            </span>
                          )}
                        </div>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody tw="bg-white divide-y divide-gray-200" className="tbody">
                {instance.getRowModel().rows.map(row => (
                  <tr key={row.id} className="group" tw="relative">
                    {props.firstCol && (
                      <td
                        css={[
                          tw`w-8`,
                          striped && tw`group-even:bg-neutral-white`,
                        ]}
                      >
                        {props.firstCol({ id: row.original.id })}
                      </td>
                    )}
                    {row.getVisibleCells().map((cell, i) => (
                      <td
                        key={cell.id}
                        style={{
                          width: cell.column.getSize(),
                        }}
                        css={[
                          tw`px-6 py-4`,
                          condensed && tw`py-1 px-1`,
                          cell.column.align === 'right' && tw`text-right`,
                          striped && tw`group-even:bg-neutral-white`,
                        ]}
                        className="td"
                      >
                        {i === 0 &&
                          props.action &&
                          props.action({ id: row.original.id })}
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
